/**
 * FishSellList
 * Container component that renders a list of FishSellCards.
 * Supports optional bulk "Sell All" button and protection locks.
 *
 * Used by both MarketSellView and QuestSellPanel for a consistent selling experience.
 */

import { FishSellCard } from './FishSellCard';
import { FishStack, Quality } from '@/types';
import { formatCurrency } from '@/data';

export interface FishSellListProps {
  /** Fish stacks to display as sell cards */
  stacks: FishStack[];
  /** Buyer/market ID for value calculations */
  buyerId: string;
  /** Function to determine max sellable per stack (default: stack.count) */
  getMaxSellable?: (stack: FishStack) => number;
  /** Callback when fish are sold */
  onSell: (definitionId: string, quality: Quality, count: number) => void;
  /** Protection state per fish: { [definitionId]: { [quality]: true } } */
  protectedFish?: Record<string, Record<string, boolean>>;
  /** Callback to toggle protection */
  onToggleProtection?: (definitionId: string, quality: Quality) => void;
  /** Bulk sell all callback - renders "Sell All Fish" button when provided */
  onSellAll?: () => void;
  /** Total value for bulk sell button display */
  totalSellableValue?: number;
  /** Custom empty state message */
  emptyMessage?: string;
  /** Custom empty state sub-message */
  emptySubMessage?: string;
}

export function FishSellList({
  stacks,
  buyerId,
  getMaxSellable,
  onSell,
  protectedFish,
  onToggleProtection,
  onSellAll,
  totalSellableValue,
  emptyMessage = 'No fish in inventory',
  emptySubMessage = 'Go catch some fish first!',
}: FishSellListProps) {
  const hasStacks = stacks.length > 0;
  const hasSellable = stacks.some((stack) => {
    const isProtected = protectedFish?.[stack.definitionId]?.[stack.quality];
    return !isProtected;
  });

  return (
    <div className="space-y-4">
      {/* Bulk Sell All Button */}
      {onSellAll && hasStacks && hasSellable && (
        <button
          onClick={onSellAll}
          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white px-6 py-4 rounded-xl font-bold transition-all transform hover:scale-105 shadow-xl text-lg"
        >
          <div className="flex items-center justify-center gap-3">
            <span className="text-2xl">💰</span>
            <span>Sell All Fish</span>
            {totalSellableValue !== undefined && totalSellableValue > 0 && (
              <span className="text-yellow-300">
                ({formatCurrency(totalSellableValue)})
              </span>
            )}
          </div>
        </button>
      )}

      {/* Protection tip */}
      {onToggleProtection && hasStacks && (
        <p className="text-sm text-gray-400">
          💡 Tip: Lock qualities to protect from Sell All & Haulers
        </p>
      )}

      {/* Sell instruction */}
      {hasStacks && (
        <p className="text-xs text-gray-500">Select quantity and sell:</p>
      )}

      {/* Fish Cards */}
      {hasStacks && (
        <div className="space-y-3">
          {stacks.map((stack) => {
            const maxSellable = getMaxSellable
              ? getMaxSellable(stack)
              : stack.count;
            const isProtected =
              protectedFish?.[stack.definitionId]?.[stack.quality] || false;

            return (
              <FishSellCard
                key={`${stack.definitionId}-${stack.quality}`}
                stack={stack}
                buyerId={buyerId}
                maxSellable={maxSellable}
                onSell={onSell}
                isProtected={isProtected}
                onToggleProtection={
                  onToggleProtection
                    ? () => onToggleProtection(stack.definitionId, stack.quality)
                    : undefined
                }
              />
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!hasStacks && (
        <div className="text-center py-8">
          <div className="text-6xl mb-4">📦</div>
          <p className="text-xl text-gray-400 mb-2">{emptyMessage}</p>
          <p className="text-sm text-gray-500">{emptySubMessage}</p>
        </div>
      )}
    </div>
  );
}
